import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Button from "../../components/Shared/Button";
import Input from "../../components/Shared/Input";
import { toast } from "react-toastify";
import Spinner from "../../components/Shared/Spinner";
import { UpdateIFRSQuestion } from "../../api/Quiz/UpdateIFRSQuestion";
import { GenerateIFRSQuestionId } from "../../api/Quiz/GenerateIFRSQuestionId";
import { GetIFRSQuestionById } from "../../api/Quiz/GetIFRSQuestionById";
import { ButtonTypes } from "../../utils/consts";

const CreateIFRSQuestion = () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { courseId, exam_id, question_id } = useParams<{
    courseId: string;
    exam_id: string;
    question_id?: string;
  }>();
  const isEditMode = !!question_id;
  const { mutate: generateQuestionId } = useMutation({
    mutationFn: async (formData: {
      description: string;
      marks?: number;
      requirements?: string;
    }) => {
      if (!exam_id) {
        throw new Error("Exam ID is required");
      }

      // Use existing question ID if available, otherwise generate a new one
      let questionId = generatedQuestionId;

      if (!questionId) {
        // Generate the question ID only if we don't have one
        const generateResponse = await GenerateIFRSQuestionId(exam_id);

        if (generateResponse.error) {
          throw new Error(
            generateResponse.error.message || "Failed to generate question ID"
          );
        }

        const newQuestionId = generateResponse.data?.data?.id;
        if (!newQuestionId) {
          throw new Error("Failed to get question ID");
        }

        questionId = newQuestionId;
        // Store the generated question ID for future use
        setGeneratedQuestionId(newQuestionId);
      }

      // Update the question with user data using the question ID
      const updateResponse = await UpdateIFRSQuestion(
        exam_id,
        questionId,
        formData
      );

      if (updateResponse.error) {
        throw new Error(
          updateResponse.error.message || "Failed to create IFRS Question"
        );
      }

      return { questionId, updateResponse };
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success("IFRS Question created successfully");
      queryClient.invalidateQueries({ queryKey: ["ifrs-questions", exam_id] });
      navigate(`/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions`);
    },
    onError: (error: Error) => {
      setIsSubmitting(false);
      toast.error(error.message || "Failed to create IFRS Question");
    },
  });
    const { mutate: updateQuestion } = useMutation({
    mutationFn: (data: {
      description: string;
      marks?: number;
      requirements?: string;
    }) => {
      if (!exam_id || !question_id) {
        throw new Error("Exam ID and Question ID are required for update");
      }
      return UpdateIFRSQuestion(exam_id, question_id, data);
    },
    onSuccess: (response) => {
      if (response.error) {
        setIsSubmitting(false);
        toast.error(response.error.message || "Failed to update IFRS Question");
        return;
      }
      setIsSubmitting(false);
      toast.success("IFRS Question updated successfully");
      queryClient.invalidateQueries({ queryKey: ["ifrs-questions", exam_id] });
      queryClient.invalidateQueries({
        queryKey: ["ifrs-question", exam_id, question_id],
      });
      navigate(`/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions`);
    },
    onError: (error: Error) => {
      setIsSubmitting(false);
      toast.error(error.message || "Failed to update IFRS Question");
    },
  });
  const [description, setDescription] = useState("");
  const [marks, setMarks] = useState("");
  const [requirements, setRequirements] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedQuestionId, setGeneratedQuestionId] = useState<string | null>(
    question_id || null
  );

  // Fetch question data if in edit mode
  const { data: QuestionResponse, isLoading: isQuestionLoading } = useQuery({
    queryFn: () => GetIFRSQuestionById(exam_id || "", question_id || ""),
    queryKey: ["ifrs-question", exam_id, question_id],
    enabled: isEditMode && !!exam_id && !!question_id,
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,

  });

  // Populate form when in edit mode
  useEffect(() => {
    if (isEditMode && QuestionResponse?.data) {
      const question = QuestionResponse.data;
      setDescription(question.description || "");
      setMarks(question.marks ? String(question.marks) : "");
      setRequirements(question.requirements || "");
    }
  }, [isEditMode, QuestionResponse]);

  if (isEditMode && isQuestionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  // Generate question ID mutation (for create mode only)


  // Update question mutation (for edit mode)


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    // if (!requirements.trim()) {
    //   toast.error("Please enter requirements");
    //   return;
    // }
    if (!exam_id) {
      toast.error("Exam ID is missing");
      return;
    }

    setIsSubmitting(true);
    const formData = {
      description,
      ...(marks ? { marks: Number(marks) } : {}),
      ...(requirements.trim() ? { requirements } : {}),
    };

    if (isEditMode) {
      // Edit mode: directly update the question
      updateQuestion(formData);
    } else {
      // Create mode: first generate ID, then update
      generateQuestionId(formData);
    }
  };

  return (
    <div className="py-7">
      <div className="flex items-center gap-4 mb-7">
        <Link
          to={
            exam_id && courseId
              ? `/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions`
              : courseId
              ? `/dashboard/courses/${courseId}/ifrs-exam`
              : "/dashboard"
          }
          className="font-semibold text-primary hover:text-primary/80"
        >
          ← Back to {exam_id ? "Questions" : "IFRS Exams"}
        </Link>
        <h1 className="text-burntBlack font-bold text-[32px]">
          {isEditMode ? "Edit IFRS Question" : "Create IFRS Question"}
        </h1>
      </div>

      <div className="p-8 bg-white shadow rounded-xl border-black/15">
        <h2 className="mb-6 text-2xl font-bold">Question Information</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div className="w-full">
              <label className="block mb-2 font-medium text-burntBlack">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="w-full">
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={description}
                  onChange={setDescription}
                  className="w-full bg-white"
                  style={{ marginBottom: "50px" }}
                />
              </div>
            </div>

            <Input
              name="marks"
              type="number"
              title="Marks"
              value={marks}
              onChange={(e) => setMarks(e.target.value)}
              placeholder="Enter marks"
              inputStyles="!rounded !h-[50px]"
            />

            <div className="w-full">
              <label className="block mb-2 font-medium text-burntBlack">
                Requirements 
              </label>
              <div className="w-full">
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={requirements}
                  onChange={setRequirements}
                  className="w-full bg-white"
                  style={{ marginBottom: "50px" }}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type={ButtonTypes.SUBMIT}
                variant="primary"
                className="!h-12 w-40 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Spinner />
                ) : isEditMode ? (
                  "Update Question"
                ) : (
                  "Create Question"
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateIFRSQuestion;
